import { ActionMail } from "../models/Action";

/**
 * Download an email as a .eml file (RFC 822/5322 format)
 * @param mailDetails - The email data to download
 * @param onSuccess - Callback to execute on successful download
 */
export const downloadEmailAsEml = (
  mailDetails: ActionMail,
  onSuccess?: () => void
): void => {
  // Format date in RFC 2822 format for email headers
  const formatEmailDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toUTCString().replace(/GMT/, "+0000");
  };

  // Encode subject for email header (handles non-ASCII characters)
  const encodeHeader = (text: string): string => {
    // For non-ASCII characters, UTF-8 encoded-word format is used
    // eslint-disable-next-line no-control-regex
    if (/[^\x00-\x7F]/.test(text)) {
      // Convert string to UTF-8 bytes, then base64 encode
      const utf8Bytes = new TextEncoder().encode(text);
      const binaryString = Array.from(utf8Bytes)
        .map((byte) => String.fromCharCode(byte))
        .join("");
      const base64 = btoa(binaryString);
      return `=?UTF-8?B?${base64}?=`;
    }
    return text;
  };

  // Convert HTML to plain text (simple version - strips tags)
  const htmlToPlainText = (html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // Check if content contains HTML tags
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(mailDetails.text);

  // Generate boundary for multipart email
  const boundary = `----=_Part_${Date.now()}_${Math.random().toString(36).substring(2)}`;

  // Prepare attachments info
  const attachmentsInfo =
    mailDetails.attachments && mailDetails.attachments.length > 0
      ? `\r\n\r\n---\r\nAttachments (${mailDetails.attachments.length}):\r\n${mailDetails.attachments
          .map(
            (att, index) =>
              `${index + 1}. ${att.filename} (${att.mimetype}, ${(att.size / 1024).toFixed(2)} KB)`
          )
          .join("\r\n")}`
      : "";

  // Build EML content with multipart if HTML, otherwise simple text
  let emlContent: string;

  if (isHtml) {
    // Multipart email with both HTML and plain text versions
    emlContent = `From: ${mailDetails.from_address}
To: ${mailDetails.to.join(", ")}
${mailDetails.cc && mailDetails.cc.length > 0 ? `Cc: ${mailDetails.cc.join(", ")}\r\n` : ""}Subject: ${encodeHeader(mailDetails.subject)}
Date: ${formatEmailDate(mailDetails.timestamp)}
Message-ID: <${mailDetails.id}@fiege-system>
MIME-Version: 1.0
Content-Type: multipart/alternative; boundary="${boundary}"
X-Fiege-Direction: ${mailDetails.direction}
X-Fiege-Counterpart: ${mailDetails.counterpart}
X-Fiege-Status: ${mailDetails.read_status || "N/A"}
X-Fiege-Type: ${mailDetails.type}
X-Fiege-Processed: ${mailDetails.processed}

--${boundary}
Content-Type: text/plain; charset=UTF-8
Content-Transfer-Encoding: 8bit

${htmlToPlainText(mailDetails.text)}${attachmentsInfo}

--${boundary}
Content-Type: text/html; charset=UTF-8
Content-Transfer-Encoding: 8bit

<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
</head>
<body>
${mailDetails.text}${attachmentsInfo.replace(/\r\n/g, "<br>")}
</body>
</html>

--${boundary}--
`.replace(/\n/g, "\r\n");
  } else {
    // Simple plain text email
    emlContent = `From: ${mailDetails.from_address}
To: ${mailDetails.to.join(", ")}
${mailDetails.cc && mailDetails.cc.length > 0 ? `Cc: ${mailDetails.cc.join(", ")}\r\n` : ""}Subject: ${encodeHeader(mailDetails.subject)}
Date: ${formatEmailDate(mailDetails.timestamp)}
Message-ID: <${mailDetails.id}@fiege-system>
MIME-Version: 1.0
Content-Type: text/plain; charset=UTF-8
Content-Transfer-Encoding: 8bit
X-Fiege-Direction: ${mailDetails.direction}
X-Fiege-Counterpart: ${mailDetails.counterpart}
X-Fiege-Status: ${mailDetails.read_status || "N/A"}
X-Fiege-Type: ${mailDetails.type}
X-Fiege-Processed: ${mailDetails.processed}

${mailDetails.text}${attachmentsInfo}
`.replace(/\n/g, "\r\n");
  }

  // Create blob with proper MIME type for email
  const blob = new Blob([emlContent], { type: "message/rfc822" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  // Create filename from subject and date
  const date = new Date(mailDetails.timestamp).toISOString().split("T")[0];
  const safeSubject = mailDetails.subject
    .replace(/[^a-z0-9]/gi, "_")
    .replace(/_+/g, "_")
    .substring(0, 50)
    .replace(/_+$/, ""); // Remove trailing underscores
  const filename = `${date}_${safeSubject || "mail"}.eml`;

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  // Execute success callback if provided
  if (onSuccess) {
    onSuccess();
  }
};
